import { useState, useEffect } from "react"
import TimeTrackingController from "../APIController/TimeTrackingController"
import Text from "./Text"

const toDateInputValue = (date) => date.toISOString().split("T")[0]

const parseAsLocal = (isoString) => {
    if (!isoString) return null
    const withZ = isoString.endsWith("Z") ? isoString : `${isoString}Z`
    return new Date(withZ)
}

const formatDateTime = (isoString) => {
    const date = parseAsLocal(isoString)
    if (!date) return null
    const datePart = date.toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    })
    const timePart = date.toLocaleTimeString("de-CH", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    })
    return `${datePart} ${timePart}`
}

const formatDuration = (ms) => {
    if (ms === null || ms < 0) return "–"
    const totalMinutes = Math.floor(ms / 1000 / 60)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours}h ${minutes}min`
}

const getDurationMs = (item) => {
    const start = parseAsLocal(item.startTime)
    const end = parseAsLocal(item.endTime)
    if (!start || !end) return null
    return end.getTime() - start.getTime()
}

const TimeTrackingReport = ({user = null}) => {
    const [entries, setEntries] = useState([])
    const today = toDateInputValue(new Date())
    const [startDate, setStartDate] = useState(today)
    const [endDate, setEndDate] = useState(today)
    const { getUserEntries, getSpecificUserEntries } = TimeTrackingController()

    useEffect(() => {
        fetchData()
    }, [startDate, endDate])

    const fetchData = async () => {
        if (!startDate || !endDate) {
            setEntries([])
            return
        }
        const endOfDay = new Date(endDate)
        endOfDay.setDate(endOfDay.getDate() + 1)
        endOfDay.setHours(0, 0, 0, 0)
        if (user !== null && user !== undefined && user.uniqueIdentifier) {
            const data = await getSpecificUserEntries(user.uniqueIdentifier, new Date(startDate), endOfDay)
            setEntries(data ?? [])
        } else {
            const data = await getUserEntries(new Date(startDate), endOfDay)
            setEntries(data ?? [])
        }
    }

    const totalMs = (entries ?? []).reduce((sum, item) => {
        const duration = getDurationMs(item)
        return duration ? sum + duration : sum
    }, 0)

    return (
        <div className="w-full h-full flex flex-col">
            <Text type="maintitle" classNames="w-full flex shrink-0">Gearbeitete Zeiten</Text>
            <div className="flex gap-3 p-3 h-min w-full shrink-0">
                <div className="flex flex-col">
                    <label className="text-sm text-gray-600">Von</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border rounded px-2 py-1"
                    />
                </div>
                <div className="flex flex-col">
                    <label className="text-sm text-gray-600">Bis</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border rounded px-2 py-1"
                    />
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 p-3">
                {entries.map((item) => (
                    <div key={item.id ?? item.offlineId} className="w-full flex justify-between border-b py-2 gap-3">
                        <div className="flex flex-col gap-2">
                            <Text type="itemtext">{formatDateTime(item.startTime)}</Text>
                            <Text type="itemtext">
                                {item.endTime ? formatDateTime(item.endTime) : "Läuft noch"}
                            </Text>
                        </div>

                        <Text type="itemtext">{formatDuration(getDurationMs(item))}</Text>
                    </div>
                ))}
            </div>

            <div className="w-full flex justify-end p-3 border-t">
                <Text type="itemmain">{`Total: ${formatDuration(totalMs)}`}</Text>
            </div>
        </div>
    )
}
export default TimeTrackingReport